import type { ReactNode } from "react";
import {
  Building2,
  Calendar,
  FileText,
  Globe,
  IdCard,
  Mail,
  MapPin,
  Phone,
  User,
} from "lucide-react";
import { formatAuditTimestamp } from "@/lib/auditFormat";
import { formatIsoDateShort } from "@/lib/bookingDates";
import { guestTypeLabel } from "@/lib/guestType";
import { PlaceBanBadge } from "@/components/bookings/PlaceBanNotice";
import type { Guest } from "@/types/camping";

function InfoItem({
  icon,
  label,
  value,
}: {
  icon: ReactNode;
  label: string;
  value: ReactNode;
}) {
  if (value == null || value === "" || value === "—") return null;
  return (
    <div className="flex gap-3">
      <span className="mt-0.5 shrink-0 text-label-tertiary">{icon}</span>
      <div className="min-w-0">
        <p className="text-xs font-medium uppercase tracking-wide text-label-tertiary">{label}</p>
        <p className="mt-0.5 text-sm text-label whitespace-pre-wrap">{value}</p>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-3">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-label-secondary">{title}</h3>
      <div className="space-y-3 rounded-xl border border-separator bg-fill-tertiary/30 p-4">
        {children}
      </div>
    </section>
  );
}

function guestDisplayName(guest: Guest): string {
  const parts = [guest.title, guest.first_name, guest.middle_name, guest.last_name].filter(Boolean);
  return parts.join(" ");
}

function formatAddress(guest: Guest): string | null {
  const streetLine = [guest.street, guest.house_number].filter(Boolean).join(" ");
  const cityLine = [guest.postal_code, guest.city].filter(Boolean).join(" ");
  const lines = [streetLine, guest.address_line_2, cityLine, guest.state_region, guest.country_code]
    .filter(Boolean)
    .join("\n");
  return lines || null;
}

export function GuestOverviewPanel({ guest }: { guest: Guest }) {
  const erasedAt = guest.privacy_erased_at ? formatAuditTimestamp(guest.privacy_erased_at) : null;
  return (
    <div className="space-y-5">
      {erasedAt ? (
        <div className="rounded-lg border border-status-orange/40 bg-status-orange/10 px-4 py-3 text-sm text-label">
          <p className="font-medium">Stammdaten gelöscht am {erasedAt}</p>
          <p className="mt-1 text-label-secondary">
            Kontaktdaten sind entfernt. Rechnungen und Zahlungen bleiben für die
            Aufbewahrungsfrist erhalten und sind in der Rechnungssuche über den Namen auf dem Beleg
            zu finden. Neue Buchungen mit diesem Gast sind nicht möglich.
          </p>
        </div>
      ) : null}
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-xl font-semibold text-label">{guestDisplayName(guest)}</h2>
        {guest.is_place_banned ? <PlaceBanBadge prominent /> : null}
        <span className="rounded-full bg-fill-tertiary px-2.5 py-0.5 text-xs font-medium text-label-secondary">
          {guestTypeLabel(guest.guest_type)}
        </span>
      </div>

      {guest.is_place_banned && guest.place_ban_reason ? (
        <div className="rounded-lg border border-status-red/40 bg-status-red/10 px-4 py-3 text-sm text-status-red">
          <span className="font-bold">Platzverbot: </span>
          {guest.place_ban_reason}
        </div>
      ) : null}

      <div className="grid gap-5 lg:grid-cols-2">
        <Section title="Persönliche Daten">
          <InfoItem icon={<User className="h-4 w-4" />} label="Name" value={guestDisplayName(guest)} />
          <InfoItem
            icon={<Calendar className="h-4 w-4" />}
            label="Geburtsdatum"
            value={guest.date_of_birth ? formatIsoDateShort(guest.date_of_birth) : null}
          />
          <InfoItem
            icon={<Globe className="h-4 w-4" />}
            label="Nationalität"
            value={guest.nationality}
          />
          <InfoItem
            icon={<IdCard className="h-4 w-4" />}
            label="Ausweisnummer"
            value={guest.id_document_number}
          />
        </Section>

        <Section title="Kontakt">
          <InfoItem icon={<Mail className="h-4 w-4" />} label="E-Mail" value={guest.email} />
          <InfoItem icon={<Phone className="h-4 w-4" />} label="Telefon" value={guest.phone} />
          <InfoItem
            icon={<Phone className="h-4 w-4" />}
            label="Mobil"
            value={guest.phone_mobile}
          />
          <InfoItem icon={<MapPin className="h-4 w-4" />} label="Adresse" value={formatAddress(guest)} />
        </Section>

        <Section title="Firma">
          <InfoItem
            icon={<Building2 className="h-4 w-4" />}
            label="Firmenname"
            value={guest.company_name}
          />
          <InfoItem icon={<FileText className="h-4 w-4" />} label="USt-IdNr." value={guest.vat_id} />
          <InfoItem
            icon={<FileText className="h-4 w-4" />}
            label="Leitweg-ID"
            value={guest.leitweg_id}
          />
        </Section>

        <Section title="System">
          <InfoItem
            icon={<Calendar className="h-4 w-4" />}
            label="Angelegt"
            value={formatAuditTimestamp(guest.created_at)}
          />
          {guest.updated_at ? (
            <InfoItem
              icon={<Calendar className="h-4 w-4" />}
              label="Zuletzt geändert"
              value={formatAuditTimestamp(guest.updated_at)}
            />
          ) : null}
        </Section>
      </div>

      {guest.notes ? (
        <Section title="Notizen">
          <p className="text-sm text-label whitespace-pre-wrap">{guest.notes}</p>
        </Section>
      ) : null}
    </div>
  );
}
